<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="sortedMergedGroupAccesses"
      hide-actions
      :class="{'less-padding': $vuetify.breakpoint.xs}"
      data-cy="groupaccess-permissions-table"
    >
      <template
        slot="items"
        slot-scope="props"
      >
        <td style="word-break: break-word; width: 400px">
          {{ props.item.group.name }}
        </td>
        <td>
          <v-layout
            v-bind="layout"
            justify-start
            class="full-height"
          >
            <!-- Contents: Read -->
            <v-btn
              icon
              dark
              flat
              class="ml-0"
              data-cy="toggle-contents-read"
              :title="$t('userManagementGroupAccessContentsRead')"
              :color="props.item.id ? colors.active : colors.inactive"
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              @click="
                isAllowedToModify
                  ? onGroupAccessClickedContentsRead(props.item)
                  : null
              "
            >
              <v-icon medium>
                visibility
              </v-icon>
            </v-btn>
            <!-- Contents: Write -->
            <v-btn
              icon
              flat
              dark
              data-cy="toggle-contents-write"
              :title="$t('userManagementGroupAccessContentsWrite')"
              :class="{'ml-0': $vuetify.breakpoint.xs}"
              :color="
                props.item.id && props.item.can_manage_content
                  ? colors.active
                  : colors.inactive
              "
              :disabled="
                !groupIdToAllowances[props.item.group.id] ||
                  !groupIdToAllowances[props.item.group.id]['contents-write']
              "
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              @click="
                isAllowedToModify
                  ? onGroupAccessClickedContentsWrite(props.item)
                  : null
              "
            >
              <v-icon medium>
                edit
              </v-icon>
            </v-btn>
          </v-layout>
        </td>
        <td>
          <v-layout
            v-bind="layout"
            justify-start
            class="full-height"
          >
            <!-- Stores: Read -->
            <v-btn
              icon
              dark
              flat
              data-cy="toggle-stores-read"
              :title="$t('userManagementGroupAccessStoresRead')"
              class="ml-0"
              :color="
                props.item.id && props.item.role === GA_ACCESS_ROLE_STORE
                  ? colors.active
                  : colors.inactive
              "
              :disabled="
                !groupIdToAllowances[props.item.group.id] ||
                  !groupIdToAllowances[props.item.group.id]['stores-read']
              "
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              @click="
                isAllowedToModify
                  ? onGroupAccessClickedStoresRead(props.item)
                  : null
              "
            >
              <v-icon medium>
                visibility
              </v-icon>
            </v-btn>
            <!-- Stores: Write -->
            <v-btn
              icon
              dark
              flat
              data-cy="toggle-stores-write"
              :title="$t('userManagementGroupAccessStoresWrite')"
              :class="{'ml-0': $vuetify.breakpoint.xs}"
              :color="
                props.item.id &&
                  props.item.role === GA_ACCESS_ROLE_STORE &&
                  props.item.level === GA_ACCESS_LEVEL_FULL
                  ? colors.active
                  : colors.inactive
              "
              :disabled="
                !groupIdToAllowances[props.item.group.id] ||
                  !groupIdToAllowances[props.item.group.id]['stores-write']
              "
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              @click="
                isAllowedToModify
                  ? onGroupAccessClickedStoresWrite(props.item)
                  : null
              "
            >
              <v-icon medium>
                edit
              </v-icon>
            </v-btn>
            <!-- Stores: Visitorcount -->
            <v-btn
              icon
              dark
              flat
              data-cy="toggle-stores-visitorcount"
              :title="$t('visitorcount')"
              :class="{'ml-0': $vuetify.breakpoint.xs}"
              :color="
                props.item.id && props.item.can_access_visitorcount
                  ? colors.active
                  : colors.inactive
              "
              :disabled="
                !groupIdToAllowances[props.item.group.id] ||
                  !groupIdToAllowances[props.item.group.id]['stores-visitorcount']
              "
              :style="{cursor: isAllowedToModify ? 'pointer' : 'not-allowed'}"
              @click="
                isAllowedToModify
                  ? onGroupAccessClickedStoresVisitorcount(props.item)
                  : null
              "
            >
              <v-icon medium>
                timeline
              </v-icon>
            </v-btn>
          </v-layout>
        </td>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import orderBy from 'lodash.orderby';

import accessConstants from '@/mixins/accessConstants.js';

export default {
  name: 'GroupAccessPermissionsTable',
  mixins: [accessConstants],
  props: {
    user: {
      type: Object,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    /** The GroupAccess objects the User's creator has. */
    availableGroupaccesses: {
      type: Array[Object],
      required: true,
    },
    /** The GroupAccess objects the User currently has. */
    actualGroupaccesses: {
      type: Array[Object],
      required: true,
    },
  },
  data() {
    return {
      colors: {
        inactive: 'grey lighten-1',
        active: 'msr',
      },
    };
  },
  computed: {
    layout() {
      if (this.$vuetify.breakpoint.xsOnly) {
        return {column: true};
      }
      return {row: true};
    },
    headers() {
      return [
        {
          text: this.$t('group'),
          value: 'group.name',
          sortable: true,
        },
        {
          text: this.$vuetify.breakpoint.xs
            ? this.$t('contents')
            : this.$t('allContents'),
          sortable: false,
        },
        {
          text: this.$vuetify.breakpoint.xs
            ? this.$t('stores')
            : this.$t('allStores'),
          sortable: false,
        },
      ];
    },
    updating: {
      get() {
        return this.busy;
      },
      set(value) {
        this.$emit('update:busy', value);
      },
    },
    /**
     * Lookup to check whether a permission can be given based on creator's access.
     *
     * The underlying philosophy is that the creator should only ever be
     * able to give access to things he himself has access to, not more.
     */
    groupIdToAllowances() {
      const groupIdToAllowances = {};
      for (let groupaccess of this.availableGroupaccesses) {
        groupIdToAllowances[groupaccess.group.id] = {
          'contents-read': true,
          'contents-write': groupaccess.can_manage_content,
          'stores-read': groupaccess.role === this.GA_ACCESS_ROLE_STORE,
          'stores-write':
            groupaccess.role === this.GA_ACCESS_ROLE_STORE &&
            groupaccess.level === this.GA_ACCESS_LEVEL_FULL,
          'stores-visitorcount': groupaccess.can_access_visitorcount,
        };
      }
      return groupIdToAllowances;
    },
    /**
     * A copied list of merged actual and available GroupAcesses.
     *
     * This is what we actually work on and modify using the buttons.
     */
    mergedGroupAccesses() {
      const groupNamesSeen = {};
      const combined = [];

      for (let groupaccess of this.actualGroupaccesses) {
        if (groupNamesSeen[groupaccess.group.name]) {
          continue;
        }
        combined.push({
          id: groupaccess.id,
          role: groupaccess.role,
          level: groupaccess.level,
          can_manage_content: groupaccess.can_manage_content,
          can_access_visitorcount: groupaccess.can_access_visitorcount,
          group: {
            id: groupaccess.group.id,
            name: groupaccess.group.name,
          },
        });
        groupNamesSeen[groupaccess.group.name] = true;
      }

      // For those that are just available, mark them accordingly via
      // id=null and just use the default values to start with.
      for (let groupaccess of this.availableGroupaccesses) {
        if (groupNamesSeen[groupaccess.group.name]) {
          continue;
        }
        combined.push({
          id: null,
          role: this.GA_ACCESS_ROLE_SPOT,
          level: this.GA_ACCESS_LEVEL_READ,
          can_manage_content: false,
          can_access_visitorcount: false,
          group: {
            id: groupaccess.group.id,
            name: groupaccess.group.name,
          },
        });
        groupNamesSeen[groupaccess.group.name] = true;
      }

      return combined;
    },
    sortedMergedGroupAccesses() {
      return orderBy(this.mergedGroupAccesses, 'group.name');
    },
    /**
     * Only a Group Manager/Coadmin may modify the managed User (but not himself).
     */
    isAllowedToModify() {
      return (
        this.$store.getters.isUsermanagerOrManagedCoadmin &&
        this.$user.id !== this.user.id
      );
    },
  },
  methods: {
    onGroupAccessClickedContentsRead(groupaccess) {
      const hasAccessAlready = !!groupaccess.id && !!groupaccess.role;
      if (hasAccessAlready) {
        // Mark for deletion.
        groupaccess.role = null;
      } else {
        groupaccess.role = this.GA_ACCESS_ROLE_SPOT;
      }
      this.onGroupAccessChanged(groupaccess);
    },
    onGroupAccessClickedContentsWrite(groupaccess) {
      groupaccess.can_manage_content = !groupaccess.can_manage_content;
      this.onGroupAccessChanged(groupaccess);
    },
    onGroupAccessClickedStoresRead(groupaccess) {
      if (groupaccess.role === this.GA_ACCESS_ROLE_SPOT) {
        groupaccess.role = this.GA_ACCESS_ROLE_STORE;
      } else {
        groupaccess.role = this.GA_ACCESS_ROLE_SPOT;
        groupaccess.level = this.GA_ACCESS_LEVEL_READ;
        groupaccess.can_access_visitorcount = false;
      }
      this.onGroupAccessChanged(groupaccess);
    },
    onGroupAccessClickedStoresWrite(groupaccess) {
      if (groupaccess.level === this.GA_ACCESS_LEVEL_READ) {
        groupaccess.role = this.GA_ACCESS_ROLE_STORE;
        groupaccess.level = this.GA_ACCESS_LEVEL_FULL;
      } else {
        groupaccess.level = this.GA_ACCESS_LEVEL_READ;
      }
      this.onGroupAccessChanged(groupaccess);
    },
    onGroupAccessClickedStoresVisitorcount(groupaccess) {
      groupaccess.can_access_visitorcount =
        !groupaccess.can_access_visitorcount;
      if (groupaccess.can_access_visitorcount) {
        groupaccess.role = this.GA_ACCESS_ROLE_STORE;
      }
      this.onGroupAccessChanged(groupaccess);
    },
    onGroupAccessChanged(groupaccess) {
      let promise;
      if (!groupaccess.id) {
        promise = this.createManagedUserGroupAccess(
          this.user,
          groupaccess.group.id,
          groupaccess.role,
          groupaccess.level,
          groupaccess.can_manage_content,
          groupaccess.can_access_visitorcount
        );
      } else {
        const canBeDeleted = groupaccess.role === null;
        if (canBeDeleted) {
          promise = this.deleteManagedUserGroupAccess(
            this.user,
            groupaccess.id
          );
        } else {
          promise = this.updateManagedUserGroupAccess(this.user, groupaccess);
        }
      }
      promise.then(() => {
        // Maybe add some visual feedback in here?
      });
    },
    /**
     * Create a new GroupAccess for the managed User.
     *
     * This will automatically be validated against the creator's
     * GroupAccesses so that it does lie within the allowed scope.
     */
    createManagedUserGroupAccess(
      user,
      groupId,
      role,
      level,
      canManageContent,
      canAccessVisitorcount
    ) {
      this.updating = true;
      return this.$api
        .createManagedUserGroupAccess(
          user.id,
          groupId,
          role,
          level,
          canManageContent,
          canAccessVisitorcount
        )
        .then((response) => {
          const groupaccess = response.data;
          this.$store.commit('ADD_GROUPACCESS_TO_MANAGED_USER', {
            user: user,
            groupaccess: groupaccess,
          });
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.updating = false;
        });
    },
    updateManagedUserGroupAccess(user, groupaccess) {
      this.updating = true;
      return this.$api
        .updateManagedUserGroupAccess(user.id, groupaccess.id, groupaccess)
        .then((response) => {
          // Do nothing.
          const groupaccess = response.data;
          this.$store.commit('UPDATE_GROUPACCESS_OF_MANAGED_USER', {
            user: user,
            groupaccess: groupaccess,
          });
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.updating = false;
        });
    },
    deleteManagedUserGroupAccess(user, groupaccessId) {
      this.updating = true;
      return this.$api
        .deleteManagedUserGroupAccess(user.id, groupaccessId)
        .then(() => {
          this.$store.commit('REMOVE_GROUPACCESS_FROM_MANAGED_USER', {
            user: user,
            groupaccessId: groupaccessId,
          });
        })
        .catch((error) => {
          console.error(error);

          this.$bus.$emit('alert-error', {
            message: 'genericFailFeedback',
            translate: true,
          });
        })
        .finally(() => {
          this.updating = false;
        });
    },
  },
};
</script>

<style scoped>
.less-padding th,
.less-padding td {
  padding: 16px !important;
}
</style>



// WEBPACK FOOTER //
// src/components/UserManagement/GroupAccessPermissionsTable.vue